// app/src/modules/ui/field.tsx — minimal, non-intrusive helpers for accessible field errors.
// Deliberately tiny: this is NOT a form framework. It changes no event flow, refs, values or
// layout — it only produces stable ids, the correct aria-* wiring, and (optionally) a small
// announced error line. Existing error banners can keep their markup and just borrow the ids.
import { type ReactNode } from "react";

/** Join truthy ids into a single aria-describedby string (or undefined when none apply). */
export function describedBy(...ids: (string | false | null | undefined)[]): string | undefined {
  const v = ids.filter(Boolean).join(" ");
  return v || undefined;
}

/** ARIA props for a control that may be invalid.
 *  - `aria-invalid` is present ONLY while invalid (never `false`, which some SRs still announce).
 *  - the error id is appended to aria-describedby only while invalid, preserving any hint id. */
export function fieldErrorProps(invalid: boolean, errorId: string, hintId?: string) {
  return {
    "aria-invalid": invalid ? true : undefined,
    "aria-describedby": describedBy(hintId, invalid ? errorId : undefined),
  } as const;
}

/** Small, announced error line. Renders nothing when there is no message, so it can sit in a
 *  layout that already reserves space without forcing a jump. Token-styled (light/dark safe). */
export function FieldError({ id, children }: { id: string; children?: ReactNode }) {
  if (!children) return null;
  return <div id={id} role="alert" style={{ color: "var(--accent-text)", fontSize: 13, fontWeight: 600, marginTop: 4 }}>{children}</div>;
}

/* ─────────────────────── Field primitives ───────────────────────
   Everything above stays: it is the ARIA wiring, and existing screens borrow
   it without changing their markup. What follows is the layer that was
   missing — the reason nine different label styles existed is that there was
   nowhere to put one.

   These render CSS classes, not inline styles. A primitive that returns
   style={{…}} would move the inline-style count sideways rather than down.   */

import { useId } from "react";

type Ctrl = { id: string; className: string } & ReturnType<typeof fieldErrorProps>;

/** Label · required/optional mark · control · help · error, wired together.
 *
 *  `children` is a render prop so the control keeps its own ref, value and
 *  event handlers — this is a layout and accessibility wrapper, not a form
 *  framework, and it must not take ownership of state.
 *
 *  Requirement is stated in the LABEL. A placeholder saying "required"
 *  disappears the moment someone types, so it cannot be the only place it is
 *  said. Mark whichever is rarer: if most fields are required, pass
 *  `optional` on the few that are not rather than starring everything else.  */
export function Field({ label, required, optional, help, error, children }: {
  label: ReactNode;
  required?: boolean;
  /** Shows a quiet "optional" instead of a required star. */
  optional?: boolean;
  help?: ReactNode;
  /** Message to show. Presence of a string is what marks the field invalid. */
  error?: string | null;
  children: (control: Ctrl) => ReactNode;
}) {
  const uid = useId();
  const id = `f-${uid}`, errId = `${id}-err`, hintId = `${id}-help`;
  const invalid = !!error;
  return (
    <div className="f-field">
      <label className="f-label" htmlFor={id}>
        {label}
        {required && <span className="f-req" aria-hidden>*</span>}
        {required && <span className="sr-only">(required)</span>}
        {optional && !required && <span className="f-opt">optional</span>}
      </label>
      {children({
        id,
        className: "f-control",
        ...fieldErrorProps(invalid, errId, help ? hintId : undefined),
      })}
      {help && <div id={hintId} className="f-help">{help}</div>}
      <FieldError id={errId}>{error}</FieldError>
    </div>
  );
}

/** A checkbox or radio whose whole row is the hit target, not just the box.
 *  Technicians use these in gloves; a 17px box alone is not a usable target. */
export function CheckField({ label, disabled, ...input }: {
  label: ReactNode; disabled?: boolean;
  checked?: boolean; onChange?: React.ChangeEventHandler<HTMLInputElement>;
  type?: "checkbox" | "radio"; name?: string; value?: string;
}) {
  return (
    <label className="f-check">
      <input type="checkbox" disabled={disabled} {...input} />
      <span>{label}</span>
    </label>
  );
}

/** Two fields side by side above 560px, stacked below. */
export function FieldRow({ children }: { children: ReactNode }) {
  return <div className="f-row f-row-2">{children}</div>;
}

/** Render a label string that carries its marker as text — "Full name *" or
 *  "Auth code (optional)" — as proper markup.
 *
 *  Twenty-nine labels across the app were written this way, spread over four
 *  local Field helpers. A screen reader reads "Full name star"; the marker
 *  cannot be styled, found or counted (my own audit reported Catalogue as
 *  having zero required markers when it had three).
 *
 *  Normalising inside the helpers fixes all of them without editing twenty-nine
 *  call sites, and leaves the source readable. New code should still pass
 *  `required`/`optional` as props — this is for the existing corpus. */
export function LabelText({ label }: { label: string }) {
  const req = /\s*\*\s*$/.test(label);
  const opt = /\s*\((optional)\)\s*$/i.test(label);
  const text = label.replace(/\s*\*\s*$/, "").replace(/\s*\((optional)\)\s*$/i, "").trim();
  return (
    <>
      {text}
      {req && <><span className="f-req" aria-hidden>*</span><span className="sr-only">(required)</span></>}
      {opt && <span className="f-opt">optional</span>}
    </>
  );
}
